// Spectral band definitions and conversion from band reflectance to display colour.
//
// The engine represents light and pigment optics in BAND_COUNT bands between
// 400 nm and 700 nm. Colour matching functions use the analytic multi-lobe fit
// of the CIE 1931 2-degree observer published by Wyman, Sloan and Shirley (JCGT 2013).
// The equal-energy white of that fit is chromatically adapted to D65 with the
// Bradford transform, then converted to linear sRGB (IEC 61966-2-1 matrix).
// The result: a perfect white reflector maps exactly to sRGB (1, 1, 1).

export const BAND_COUNT = 12;
export const LAMBDA_MIN = 400;
export const LAMBDA_MAX = 700;
export const BAND_WIDTH = (LAMBDA_MAX - LAMBDA_MIN) / BAND_COUNT;

// Band centre wavelengths in nm.
export const BAND_CENTRES = Array.from({ length: BAND_COUNT }, (_, i) => LAMBDA_MIN + BAND_WIDTH * (i + 0.5));

function lobe(lambda, mu, sigmaLow, sigmaHigh) {
  const sigma = lambda < mu ? sigmaLow : sigmaHigh;
  const t = (lambda - mu) / sigma;
  return Math.exp(-0.5 * t * t);
}

export function cieX(lambda) {
  return 1.056 * lobe(lambda, 599.8, 37.9, 31.0) + 0.362 * lobe(lambda, 442.0, 16.0, 26.7) - 0.065 * lobe(lambda, 501.1, 20.4, 26.2);
}

export function cieY(lambda) {
  return 0.821 * lobe(lambda, 568.8, 46.9, 40.5) + 0.286 * lobe(lambda, 530.9, 16.3, 31.1);
}

export function cieZ(lambda) {
  return 1.217 * lobe(lambda, 437.0, 11.8, 36.0) + 0.681 * lobe(lambda, 459.0, 26.0, 13.8);
}

const BRADFORD = [
  [0.8951, 0.2664, -0.1614],
  [-0.7502, 1.7135, 0.0367],
  [0.0389, -0.0685, 1.0296],
];

const XYZ_TO_LINEAR_SRGB = [
  [3.2406, -1.5372, -0.4986],
  [-0.9689, 1.8758, 0.0415],
  [0.0557, -0.2040, 1.0570],
];

const D65_WHITE = [0.95047, 1.0, 1.08883];

function mat3Mul(a, b) {
  return a.map((row) => [0, 1, 2].map((j) => row[0] * b[0][j] + row[1] * b[1][j] + row[2] * b[2][j]));
}

function mat3Vec(m, v) {
  return m.map((row) => row[0] * v[0] + row[1] * v[1] + row[2] * v[2]);
}

function mat3Inverse(m) {
  const [a, b, c] = m[0];
  const [d, e, f] = m[1];
  const [g, h, i] = m[2];
  const A = e * i - f * h;
  const B = -(d * i - f * g);
  const C = d * h - e * g;
  const det = a * A + b * B + c * C;
  return [
    [A / det, -(b * i - c * h) / det, (b * f - c * e) / det],
    [B / det, (a * i - c * g) / det, -(a * f - c * d) / det],
    [C / det, -(a * h - b * g) / det, (a * e - b * d) / det],
  ];
}

// Integrates the colour matching functions over each band (1 nm sub-steps) so
// coarse bands still weight the observer correctly.
function computeBandXyzWeights() {
  const weights = { x: new Float64Array(BAND_COUNT), y: new Float64Array(BAND_COUNT), z: new Float64Array(BAND_COUNT) };
  for (let band = 0; band < BAND_COUNT; band += 1) {
    const start = LAMBDA_MIN + band * BAND_WIDTH;
    for (let l = start + 0.5; l < start + BAND_WIDTH; l += 1) {
      weights.x[band] += cieX(l);
      weights.y[band] += cieY(l);
      weights.z[band] += cieZ(l);
    }
  }
  return weights;
}

function buildBandToRgbMatrix() {
  const w = computeBandXyzWeights();
  let whiteX = 0;
  let whiteY = 0;
  let whiteZ = 0;
  for (let band = 0; band < BAND_COUNT; band += 1) {
    whiteX += w.x[band];
    whiteY += w.y[band];
    whiteZ += w.z[band];
  }
  const sourceWhite = [whiteX / whiteY, 1, whiteZ / whiteY];
  // Bradford adaptation from the fit's equal-energy white to D65.
  const coneSource = mat3Vec(BRADFORD, sourceWhite);
  const coneTarget = mat3Vec(BRADFORD, D65_WHITE);
  const scale = [
    [coneTarget[0] / coneSource[0], 0, 0],
    [0, coneTarget[1] / coneSource[1], 0],
    [0, 0, coneTarget[2] / coneSource[2]],
  ];
  const adapt = mat3Mul(mat3Inverse(BRADFORD), mat3Mul(scale, BRADFORD));
  const toRgb = mat3Mul(XYZ_TO_LINEAR_SRGB, adapt);
  const matrix = [new Float32Array(BAND_COUNT), new Float32Array(BAND_COUNT), new Float32Array(BAND_COUNT)];
  for (let band = 0; band < BAND_COUNT; band += 1) {
    const xyz = [w.x[band] / whiteY, w.y[band] / whiteY, w.z[band] / whiteY];
    const rgb = mat3Vec(toRgb, xyz);
    matrix[0][band] = rgb[0];
    matrix[1][band] = rgb[1];
    matrix[2][band] = rgb[2];
  }
  // Remove the residual of the 4-digit published matrices so white is exactly (1,1,1).
  for (let channel = 0; channel < 3; channel += 1) {
    let sum = 0;
    for (let band = 0; band < BAND_COUNT; band += 1) sum += matrix[channel][band];
    for (let band = 0; band < BAND_COUNT; band += 1) matrix[channel][band] /= sum;
  }
  return matrix;
}

// BAND_TO_LINEAR_RGB[channel][band]: linear sRGB from a reflectance spectrum under D65.
export const BAND_TO_LINEAR_RGB = buildBandToRgbMatrix();

export function spectrumToLinearRgb(spectrum, out = [0, 0, 0]) {
  const m = BAND_TO_LINEAR_RGB;
  let r = 0;
  let g = 0;
  let b = 0;
  for (let band = 0; band < BAND_COUNT; band += 1) {
    const value = spectrum[band];
    r += m[0][band] * value;
    g += m[1][band] * value;
    b += m[2][band] * value;
  }
  out[0] = r;
  out[1] = g;
  out[2] = b;
  return out;
}

export function linearToSrgb(value) {
  const v = value <= 0 ? 0 : value >= 1 ? 1 : value;
  return v <= 0.0031308 ? 12.92 * v : 1.055 * Math.pow(v, 1 / 2.4) - 0.055;
}

export function srgbToLinear(value) {
  const v = value <= 0 ? 0 : value >= 1 ? 1 : value;
  return v <= 0.04045 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
}

export function hexToSrgb(hex) {
  const raw = String(hex || '').trim().replace('#', '');
  const full = raw.length === 3 ? raw.split('').map((c) => c + c).join('') : raw.padEnd(6, '0').slice(0, 6);
  const n = Number.parseInt(full, 16);
  if (!Number.isFinite(n)) return [0, 0, 0];
  return [((n >> 16) & 255) / 255, ((n >> 8) & 255) / 255, (n & 255) / 255];
}

export function srgbToHex(rgb) {
  return `#${rgb.map((v) => Math.round(Math.max(0, Math.min(1, v)) * 255).toString(16).padStart(2, '0')).join('')}`;
}

// CIELAB (D65) from linear sRGB, for colour-difference measurements in tests and reports.
const LINEAR_SRGB_TO_XYZ = mat3Inverse(XYZ_TO_LINEAR_SRGB);

export function linearRgbToLab(rgb) {
  const xyz = mat3Vec(LINEAR_SRGB_TO_XYZ, rgb);
  const f = (t) => (t > 216 / 24389 ? Math.cbrt(t) : (t * 24389 / 27 + 16) / 116);
  const fx = f(xyz[0] / D65_WHITE[0]);
  const fy = f(xyz[1] / D65_WHITE[1]);
  const fz = f(xyz[2] / D65_WHITE[2]);
  return [116 * fy - 16, 500 * (fx - fy), 200 * (fy - fz)];
}

// CIE76 colour difference. Adequate for regression comparisons; not a perceptual calibration.
export function deltaE76(labA, labB) {
  const dl = labA[0] - labB[0];
  const da = labA[1] - labB[1];
  const db = labA[2] - labB[2];
  return Math.sqrt(dl * dl + da * da + db * db);
}

// Planck's law, integrated against the observer, gives the RGB tint of a
// black-body light source at the given colour temperature (normalised so the
// brightest channel is 1). Used for the light-temperature control.
export function blackbodyLinearRgb(kelvin) {
  const T = Math.max(1000, Math.min(40000, Number(kelvin) || 6500));
  const c2 = 1.4388e7; // second radiation constant, nm*K
  const spectrum = new Float64Array(BAND_COUNT);
  let peak = 0;
  for (let band = 0; band < BAND_COUNT; band += 1) {
    const l = BAND_CENTRES[band];
    spectrum[band] = 1 / (Math.pow(l, 5) * (Math.exp(c2 / (l * T)) - 1));
    peak = Math.max(peak, spectrum[band]);
  }
  for (let band = 0; band < BAND_COUNT; band += 1) spectrum[band] /= peak;
  const rgb = spectrumToLinearRgb(spectrum);
  const max = Math.max(rgb[0], rgb[1], rgb[2], 1e-6);
  return rgb.map((v) => Math.max(0, v / max));
}
